import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';

import { DateTime } from 'luxon';
import { SelectOption } from 'src/app/core/component/form-inputs/select-input/select-input.component';
import { Log } from 'src/app/core/log';

export interface DateFilter {
  startDate: Date;
  endDate: Date;
  startDateObj: DateTime;
  endDateObj: DateTime;
  startDateKey: number;
  endDateKey: number;
  startDateIso: string;
  endDateIso: string;
}

enum DateRange {
  AllTime = 1,
  Today = 2,
  SevenDays = 3,
  ThirtyDays = 4,
  NinetyDays = 5,
  HalfYear = 6,
  Year = 7,
  Custom = 8
}

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
})
export class DateFilterComponent implements OnInit {

  @Output() dateFilterChanged: EventEmitter<DateFilter> = new EventEmitter<DateFilter>();

  @Input() applyOnEdit: boolean = false
  @Input() earliestDate: DateTime;

  get DateRange() { return DateRange; }

  dateRangeOpts: SelectOption[] = [];
  selDateRange: DateRange = DateRange.ThirtyDays;

  startDateString: string;
  endDateString: string;

  constructor(
  ) {
    // initialize data filter to last 30 days
    const today = DateTime.local();
    const start = today.minus({ days: 30 });

    this.startDateString = start.toFormat('yyyy-MM-dd');
    this.endDateString = today.toFormat('yyyy-MM-dd');

    this.dateRangeOpts.push({ value: DateRange.SevenDays, displayValue: "Past 7 Days" });
    this.dateRangeOpts.push({ value: DateRange.ThirtyDays, displayValue: "Past 30 Days" });
    this.dateRangeOpts.push({ value: DateRange.NinetyDays, displayValue: "Past 90 Days" });
    this.dateRangeOpts.push({ value: DateRange.HalfYear, displayValue: "Past 180 Days" });
    this.dateRangeOpts.push({ value: DateRange.Year, displayValue: "Past Year" });
    this.dateRangeOpts.push({ value: DateRange.AllTime, displayValue: "All Time" });
    this.dateRangeOpts.push({ value: DateRange.Today, displayValue: "Today" });
    this.dateRangeOpts.push({ value: DateRange.Custom, displayValue: "Custom Range" });

    const savedFilter = localStorage.getItem('dfilter');
    if (savedFilter != null) {
      this.selDateRange = +savedFilter;

      if (this.selDateRange == DateRange.Custom) {
        const savedCustomFrom = localStorage.getItem('dfilterF');
        const saveCustomTo = localStorage.getItem('dfilterT');
        if (savedCustomFrom) {
          this.startDateString = savedCustomFrom
        }
        if (savedCustomFrom) {
          this.endDateString = saveCustomTo
        }
      }
    }
  }

  ngOnInit(): void {
    this.dateRangeChanged();
  }

  dateRangeChanged() {
    const today = DateTime.local();
    let start = today.minus({ days: 7 });
    let end = today;
    localStorage.setItem('dfilter', this.selDateRange.toString());
    switch (this.selDateRange) {
      case DateRange.SevenDays:
        break; // default, nothing else to do
      case DateRange.ThirtyDays:
        start = today.minus({ days: 30 });
        break;
      case DateRange.NinetyDays:
        start = today.minus({ days: 90 });
        break;
      case DateRange.HalfYear:
        start = today.minus({ days: 180 });
        break;
      case DateRange.Year:
        start = today.minus({ days: 365 });
        break;
      case DateRange.AllTime:
        start = today.minus({ days: 5000 });
        //end = today.plus({days:5000}); // don't go into the future
        break;
      case DateRange.Custom:
        // if the start date is set to the default 5000 days ago, set it to 30 days ago
        if (this.startDateString != null &&
          DateTime.fromJSDate(new Date(this.startDateString)) <= today.minus({ days: 1000 })) {
          this.startDateString = today.minus({ months: 1 }).toISO();
        }
        // if the end date is in the future, set it to today
        if (this.endDateString != null &&
          DateTime.fromJSDate(new Date(this.endDateString)) > today) {
          this.endDateString = today.toISO();
        }
        this.applyFilter();
        return;
      case DateRange.Today:
        start = today;
        break;
      default:  // includes DateRange.Custom:
        // nothing to do, just revealing the start/end date inputs
        this.applyFilter();
        return;
    }

    // ensure our start date isn't before the earliest date we want to allow
    if (this.earliestDate != null && start < this.earliestDate) {
      Log.debug('start date before earliest date, adjusting, start: ' + start.toISO() + ', earliest: ' + this.earliestDate.toISO());
      start = this.earliestDate;
      Log.debug('start date after adjustment: ' + start.toISO());
    }

    this.startDateString = start.toISO();
    this.endDateString = end.toISO();

    this.applyFilter();
  }

  applyFilter(fromBlur = false) {
    if (fromBlur === true && this.applyOnEdit === false) {
      return;
    }

    let fromIso = undefined;
    let toIso = undefined;
    let fromKey = undefined;
    let toKey = undefined;
    let startDate = undefined;
    let endDate = undefined;
    let startDateObj: DateTime = undefined;
    let endDateObj: DateTime = undefined;

    if (!String.isNullOrWhiteSpace(this.endDateString)) {
      toKey= this.getKeyValForDate(this.endDateString);
      endDate = this.convertToDateObject(toKey, true);
      toIso = DateTime.fromJSDate(endDate).toISO();
      endDateObj = DateTime.fromISO(toIso);
    }

    if (!String.isNullOrWhiteSpace(this.startDateString)) {
      fromKey = this.getKeyValForDate(this.startDateString);
      startDate = this.convertToDateObject(fromKey, false);
      fromIso = DateTime.fromJSDate(startDate).toISO();
      startDateObj = DateTime.fromISO(fromIso);
    }

    //Log.debug('filter applied: ' + fromIso + ' - ' + toIso);
    this.dateFilterChanged.emit(
      {
        startDate: startDate,
        endDate: endDate,
        startDateKey: fromKey,
        endDateKey: toKey,
        startDateIso: fromIso,
        endDateIso: toIso,
        startDateObj: startDateObj,
        endDateObj: endDateObj
      }
    );
    localStorage.setItem('dfilterF', this.startDateString);
    localStorage.setItem('dfilterT', this.endDateString);
  }

  private getKeyValForDate(dateString: string) {
    let filterDate = new Date(dateString);
    const year = filterDate.getFullYear();
    const month = (filterDate.getMonth() + 1).toString().padStart(2, '0');
    const day = filterDate.getDate().toString().padStart(2, '0');
    
    return +(`${year}${month}${day}`);
  }

  private convertToDateObject(dateKey, endOfDay: boolean) {
    const dateKeyString = dateKey.toString();
    var year = dateKeyString.slice(0, 4);
    var month = dateKeyString.slice(4, 6);
    var day = dateKeyString.slice(6, 8);

    // Note: Months are zero-based in JavaScript Date objects
    if (endOfDay) {
      return new Date(year, month - 1, day, 23, 59, 59, 999);
    } else {
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }
  }
}
